import { Injectable } from '@angular/core';
import { AUTH_STATIC_PATH, SESSION_HANDLER_KEY, UNAUTH_STATIC_PATH } from '../../constants/analytics.constants';
import { v4 as uuid } from 'uuid';
import * as AWS_SDK from 'aws-sdk';
import { AuthService } from '../core/auth.service';
import { Environment } from '../../config/environment';
import * as moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { JwtHelper } from '../core/jwt-helper.service';
import {
	EventTrackingBean,
	ApptEventTrackingBean,
	ApptCancellationEventTrackingBean,
	HvaTrackingBean,
} from '../../beans/analytics/analytics-module.bean';
import { AuthSession } from '@aws-amplify/core';
import { CredentialsOptions } from 'aws-sdk/lib/credentials';

/** Data layer helper Object */
declare let DataLayerHelper: any;
/** UUID */
let RANDOM_SALT;

/** Custom Analytics Service */
@Injectable({
	providedIn: 'root',
})
export class AnalyticsService {
	private helper: any;
	/** dataLayer */
	private dataLayer = [];
	/** bucket name */
	// private bucketName: string;
	/** Holds environment variable */
	private env;

	/**
	 * Analytics Service constructor
	 * @param authService - Auth Service object
	 * @param environment - Environment Service
	 * @param http - Angular HTTP Object
	 */
	constructor(
		private authService: AuthService,
		private environment: Environment,
		private cookieService: CookieService,
		private jwtService?: JwtHelper
	) {
		this.setSessionId();
		this.helper = new DataLayerHelper(this.dataLayer, this.listener);
		this.env = this.environment.getEnvironment();
	}

	private listener(model, message) {
		// Message has been pushed.
		// The helper has merged it onto the model.
		// Now use the message and the updated model to do something.
	}

	getData(key: string) {
		return this.helper.get(key);
	}

	/**
	 * Send data to S3
	 * @param data - data to be passed
	 * @param auth - denote whether the data to be send to authenticated or un-authenticated bucket
	 */
	pushData(data) {
		const self = this;
		if (!data?.eventValue?.user?.uuid) {
			try {
				data.eventValue.user.uuid = JSON.parse(atob(sessionStorage.getItem('profile')))?.profileInfo?.epmsPID + '';
			} catch (error) {}
		}
		self.dataLayer.push(data);
		data.sessionId = RANDOM_SALT;
		data.eventValue.client = { useragent: navigator.userAgent };
		data.timestamp = Math.round(new Date().getTime() / 1000);
		this.authService
			.getCurrentAmplifyUserAuthSession()
			.then(authSession => {
				if (authSession) {
					const ssoType = sessionStorage.getItem('sso-type');
					if (!ssoType && !authSession?.tokens?.idToken) {
						self.publishTOUnAuthS3(data, authSession.credentials);
					} else {
						self.publishToS3(data, authSession, this.env.ANALYTICS_AUTH_BUCKET);
					}
				} else {
					throw new Error('AuthSession not available.');
				}
			})
			.catch(error => {
				this.authService.getUnauthCredentials().then(credentials => {
					self.publishTOUnAuthS3(data, credentials);
				});
			});
	}

	/**
	 * Publishing the data to Authenticated bucket
	 * @param data - data to be saved in S3
	 * @param credentials - holds aws credentials
	 */
	publishToS3(
		data: any,
		authSession: AuthSession,
		bucketName: string,
		fileName: string = '',
		originalConsoleErrorFn = null
	) {
		const s3 = new AWS_SDK.S3({
			signatureVersion: 'v4',
			credentials: authSession.credentials,
		});

		const year = new Date().getFullYear();
		const month = ('0' + (new Date().getMonth() + 1)).slice(-2);
		const date = new Date().getDate();

		const loc = 'year=' + year + '/month=' + month + '/day=' + date + '/';

		if (!fileName) {
			fileName = AUTH_STATIC_PATH + loc + SESSION_HANDLER_KEY + '-' + uuid() + '.json';
		}
		const params = {
			Body: JSON.stringify(data),
			Bucket: bucketName,
			Key: fileName,
			ContentType: 'application/json',
		};

		const thisRef = this;
		// console.log('+++++++  Pushing to Authorized Bucket: ' + this.bucketName + '  FileName: ' + fileName);
		s3.putObject(params, function (error, resData) {
			if (error) {
				if (originalConsoleErrorFn && typeof originalConsoleErrorFn === 'function') {
					originalConsoleErrorFn.call(console, error);
				} else {
					console.error(`[${AnalyticsService.name}][${thisRef.publishToS3.name}]`, error, error.stack);
				}
			}
		});
	}

	/**
	 * publishing the data to unauthenticated bucket
	 * @param data - data to be saved in S3
	 */
	async publishTOUnAuthS3(data, credentials?: AWS_SDK.Credentials | CredentialsOptions) {
		const bucketName = this.env.ANALYTICS_UNAUTH_BUCKET;
		if (!credentials) {
			credentials = await this.authService.getUnauthCredentials();
		}

		const s3 = new AWS_SDK.S3({
			signatureVersion: 'v4',
			credentials: credentials,
		});

		const year = new Date().getFullYear();
		const month = ('0' + (new Date().getMonth() + 1)).slice(-2);
		const date = new Date().getDate();

		const loc = 'year=' + year + '/month=' + month + '/day=' + date + '/';

		const fileName = UNAUTH_STATIC_PATH + loc + SESSION_HANDLER_KEY + '-' + uuid() + '.json';

		const params = {
			Body: JSON.stringify(data),
			Bucket: bucketName,
			Key: fileName,
			ContentType: 'application/json',
		};

		const thisRef = this;
		s3.putObject(params, function (error, resData) {
			if (error) {
				console.error(`[${AnalyticsService.name}][${thisRef.publishToS3.name}]`, error, error.stack);
			}
		});
	}

	/**
	 * Push UI logs to S3
	 * @param logs
	 * @param bucketName
	 * @param isError (default: `true`)
	 */
	pushLogs(logs: any, originalConsoleErrorFn: any, isError: boolean = true) {
		const data: any = { sessionId: '', agent: '', timestamp: '', error: '' };
		data.sessionId = RANDOM_SALT;
		data.type = sessionStorage.getItem('userId') || 'Anonymous';
		data.agent = { useragent: navigator.userAgent };
		data.timestamp = Math.round(new Date().getTime() / 1000);
		data[isError ? 'error' : 'data'] = logs;
		let email, epmsPID;
		const ssoType = sessionStorage.getItem('sso-type');
		if (!ssoType) {
			const auth = sessionStorage.getItem('auth');
			if (auth) {
				const authParsed = JSON.parse(auth);
				if (authParsed?.idToken?.payload) {
					email = authParsed.idToken.payload.email;
					epmsPID = authParsed.idToken.payload['custom:epms-p-id'];
				}
			}
		} else {
			epmsPID = this.jwtService.getEpmsId();
		}
		data.user = {
			email: email,
			uuid: epmsPID,
		};
		if (!data.user.uuid) {
			try {
				data.user.uuid = JSON.parse(atob(sessionStorage.getItem('profile')))?.profileInfo?.epmsPID + '';
			} catch (error) {}
		}
		const date = moment(new Date()).format('YYYY-MM-DD');
		const fileName = `${date}-ui-log-${RANDOM_SALT}___${uuid()}.json`;

		this.publishToS3(data, AWS_SDK.config, this.env.S3_UI_LOGS_BUCKET, fileName, originalConsoleErrorFn);
		// const thisRef = this;
		// this.pushS3.putObject(params, function (err, resData) {
		//   if (err) {
		// 		console.error(`[${AnalyticsService.name}][${thisRef.publishToS3.name}]`, err, err.stack);
		//   }
		// });
	}

	private setSessionId(): void {
		if (this.cookieService.check('myehe-analytics-session-id')) {
			RANDOM_SALT = this.cookieService.get('myehe-analytics-session-id');
		} else {
			RANDOM_SALT = uuid();
			this.cookieService.set(
				'myehe-analytics-session-id',
				RANDOM_SALT,
				new Date(new Date().getTime() + 1000 * 60 * 60)
			);
		}
	}

	sendGaEvent(eventData: EventTrackingBean) {
		eventData.event = 'buttonClick';
		eventData.eventValue = window.location.href;
		(<any>window).dataLayer?.push(eventData);
	}

	sendHvaEvent(eventData: HvaTrackingBean) {
		eventData.event = 'hva';
		(<any>window).dataLayer?.push(eventData);
	}

	sendApptConfirmationEvent(eventData: ApptEventTrackingBean) {
		eventData.event = 'apptConfirmationEvent';
		(<any>window).dataLayer?.push(eventData);
	}

	sendApptCancellationEvent(eventData: ApptCancellationEventTrackingBean) {
		eventData.event = 'apptCancelationEvent';
		(<any>window).dataLayer?.push(eventData);
	}
}
